// Global Instructions Rule Applied!
//
// Configuration → Internal Agents (admin only).
//
// The budget and model controls for the portfolio-company agents. Until this
// panel existed every one of these was a hand-written SQL migration.
//
// Five sections, in the order an admin reasons about them:
//   1. Global budget    — cfg.app_config group 'internal_agents'. The master
//      switch, the shared daily USD ceiling, the per-call timeout and the
//      dispatch concurrency.
//   2. Agent types      — cfg.internal_agent_types. Per-type per-run cost cap,
//      runs/day and memory token budget, the type-level kill switch, and the
//      follow lifecycle (design §7.5): whether gaining the auto-provision role
//      starts a backfill, how many of those may start per hour, and how long
//      following continues after the role is lost.
//   3. Task routing     — cfg.internal_agent_task_models. Which model each task
//      of each agent type calls, and the per-task call options.
//   4. Model registry   — cfg.llm_models. The models the routing can choose
//      from, and the per-million-token prices the spend accounting multiplies.
//   5. Usage today      — read-only, so the caps above are numbers with a
//      referent rather than abstractions.
//
// Chrome copied from DocumentsPanel.tsx (the newest design-pattern-compliant
// sibling): FormSection + AntD Table inside it, per-field autosave on
// blur/change, Popconfirm deletes, the standard Tag vocabulary, AntD's default
// [Cancel][Save] modal footer.
//
// No useSelector: nothing this panel needs lives in globalConfigs — the agent
// cfg is served by its own admin RPC. Adding a selector for nothing would be
// noise, so please don't "fix" its absence.
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Input, InputNumber, Modal, Popconfirm, Select, Switch, Table, Tag, Tooltip, Typography } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import dayjs from 'dayjs'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { useLookups } from '../../global_configs/hooks/useLookups.ts'
import InlineNumberField from './InlineNumberField.tsx'
import { InvestmentCriteriaSection } from './InvestmentCriteriaSection.tsx'
import JsonFieldsModal, { JsonFieldSpec } from './JsonFieldsModal.tsx'
import {
  AgentConfigValueKey,
  InternalAgentTaskModelAdminRow,
  InternalAgentTypeAdminRow,
  InternalAgentsConfigAdminData,
  LpStatusCadenceRow,
  InternalAgentsUsageData,
  LlmModelAdminRow
} from '../../../state_manager/data_models/internal_agents_config_model.ts'
import {
  InternalAgentTypeFields,
  createInternalAgentTaskModel,
  createLlmModel,
  deleteInternalAgentTaskModel,
  deleteLlmModel,
  fetchInternalAgentsConfig,
  fetchInternalAgentsUsage,
  LlmModelFields,
  updateInternalAgentTaskModel,
  updateInternalAgentType,
  updateInternalAgentsBudget,
  updateLpStatusCadence,
  LpStatusCadenceFields,
  updateLlmModel
} from '../services/internalAgentsConfigService.ts'
import { CONFIG_PANEL_SCROLL_CLASS } from '../configModules.ts'

const { Text } = Typography

const EMPTY_CONFIG: InternalAgentsConfigAdminData = {
  resolvedEnvironment: 'global',
  appConfig: [],
  lpStatusCadence: [],
  agentTypes: [],
  taskModels: [],
  models: [],
  taskKeys: [],
  providers: [],
  taskParamKeys: []
}

const EMPTY_USAGE: InternalAgentsUsageData = {
  totals: {},
  by_agent_type: [],
  by_model: [],
  by_agent_type_task: []
}

const money = (v?: number | null): string =>
  `$${Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const compactNumber = (v?: number | null): string => Number(v ?? 0).toLocaleString()

// Mirrors the cfg.internal_agent_types.schedule_frequency_unit CHECK. 'none' is
// a real setting, not an empty one: it means this type runs on interactions or
// on demand only.
const SCHEDULE_UNIT_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
]

// dayjs day() numbering: 0 = Sunday.
const WEEKDAY_OPTIONS = [0, 1, 2, 3, 4, 5, 6].map((d) => ({
  value: d,
  label: dayjs().day(d).format('dddd')
}))

// internal_agents__enqueue_due stores a DATE and reads only part of it: the
// weekday for a weekly schedule, the day-of-month for a monthly one, the phase
// for a daily one with an interval above 1. These turn the part the operator
// picked back into a concrete anchor — the most recent matching day, never a
// future one (the RPC refuses a future anchor, since it would park the schedule
// until the date arrived).
function mostRecentWeekday(day: number): string {
  const today = dayjs().startOf('day')
  const delta = (today.day() - day + 7) % 7
  return today.subtract(delta, 'day').format('YYYY-MM-DD')
}

function mostRecentDayOfMonth(day: number): string {
  let cursor = dayjs().startOf('day')
  // Walk back at most a few months: a 31st does not exist in every month, and
  // the enqueue clamps to the month end rather than skipping, so any month
  // whose length reaches `day` gives the same recurrence.
  for (let i = 0; i < 4; i += 1) {
    const candidate = cursor.date(Math.min(day, cursor.daysInMonth()))
    if (!candidate.isAfter(dayjs(), 'day') && candidate.date() === day) {
      return candidate.format('YYYY-MM-DD')
    }
    cursor = cursor.subtract(1, 'month').endOf('month')
  }
  return dayjs().startOf('day').format('YYYY-MM-DD')
}

/** The next date enqueue_due would consider this type due, as a short label.
 *  Mirrors the RPC's arithmetic — (current_date - anchor) %% (unit * interval)
 *  — so the operator sees the effect of a phase they cannot otherwise observe
 *  (a fortnightly schedule looks identical to a weekly one in the controls). */
function nextRunHint(unit: string, interval: number, anchor?: string | null): string | null {
  if (!anchor || unit === 'none') return null
  const start = dayjs(anchor).startOf('day')
  if (!start.isValid()) return null
  const today = dayjs().startOf('day')
  const step = Math.max(interval || 1, 1)
  for (let i = 0; i < 400; i += 1) {
    const day = today.add(i, 'day')
    if (unit === 'daily' && day.diff(start, 'day') % step === 0) return day.format('ddd DD MMM')
    if (unit === 'weekly' && day.diff(start, 'day') % (7 * step) === 0) return day.format('ddd DD MMM')
    if (unit === 'monthly') {
      const dom = Math.min(start.date(), day.daysInMonth())
      const months =
        (day.year() - start.year()) * 12 + (day.month() - start.month())
      if (day.date() === dom && months % step === 0) return day.format('ddd DD MMM')
    }
  }
  return null
}

/** Turn the cfg-driven key list into the modal's field spec, filtered to the
 *  provider whose client actually reads each key. */
function paramFieldSpecs(keys: AgentConfigValueKey[], provider?: string | null): JsonFieldSpec[] {
  return keys
    .filter((k) => {
      const only = k.agent_config_value_keys__applies_to_provider
      return !only || !provider || only === provider
    })
    // 'object'-typed keys (OpenAI's `reasoning`) have no scalar editor; they are
    // preserved by the modal's merge rather than offered as a field.
    .filter((k) => k.agent_config_value_keys__value_type !== 'object')
    .map((k) => {
      const allowed = Array.isArray(k.agent_config_value_keys__allowed_values)
        ? (k.agent_config_value_keys__allowed_values as string[])
        : []
      return {
        key: k.agent_config_value_keys__value_key,
        label: k.agent_config_value_keys__label,
        type: allowed.length ? 'select' : 'number',
        options: allowed.map((v) => ({ value: v, label: v })),
        min: k.agent_config_value_keys__min_value ?? undefined,
        max: k.agent_config_value_keys__max_value ?? undefined,
        hint: k.agent_config_value_keys__description ?? undefined
      } as JsonFieldSpec
    })
}

/** Compact one-line summary of a params/caps bag for a table cell. */
function bagSummary(bag: unknown): string {
  if (!bag || typeof bag !== 'object') return ''
  const entries = Object.entries(bag as Record<string, unknown>)
  if (!entries.length) return ''
  return entries
    .map(([k, v]) => `${k} ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join(' · ')
}

export default function InternalAgentsPanel(): React.ReactElement {
  const token = () => getAccessToken(store.getState().auth)

  const [data, setData] = useState<InternalAgentsConfigAdminData>(EMPTY_CONFIG)
  const [usage, setUsage] = useState<InternalAgentsUsageData>(EMPTY_USAGE)
  // Usage window (LP agent programme Phase 5 cost report): 1 = since UTC
  // midnight, the daily-cap boundary; wider windows are read-only reporting.
  const [usageDays, setUsageDays] = useState<number>(1)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Params modal (task routing)
  const [paramsRow, setParamsRow] = useState<InternalAgentTaskModelAdminRow | null>(null)
  // Add-routing modal
  const [newRouting, setNewRouting] = useState<{
    agent_type_id?: string
    task_key?: string
    llm_model_key?: string
  } | null>(null)
  // Model registry modal (create + edit)
  const [modelDraft, setModelDraft] = useState<LlmModelFields | null>(null)
  const [modelDraftId, setModelDraftId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [config, usageData] = await Promise.all([
        fetchInternalAgentsConfig(token()),
        fetchInternalAgentsUsage(token(), usageDays)
      ])
      setData(config)
      setUsage(usageData)
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load internal agent configuration')
    } finally {
      setLoading(false)
    }
  }, [usageDays])

  useEffect(() => {
    load()
  }, [load])

  // Copied from DocumentsPanel.tsx with one addition: reloadOnError.
  //
  // The 409 in-use guard on a model disable/delete is a DESIGNED outcome here,
  // not a rare edge. A controlled AntD Switch keeps its flipped DOM state until
  // a re-render arrives, and the plain helper never re-renders on the failure
  // path — so a refused disable would leave the toggle reading "off" while the
  // database says "on". Reloading on error snaps every control back to the
  // truth the server just asserted.
  const run = async (
    action: () => Promise<void>,
    failure: string,
    reloadOnError = false
  ): Promise<boolean> => {
    try {
      await action()
      await load()
      return true
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : failure)
      if (reloadOnError) await load()
      return false
    }
  }

  // ---- global budget ------------------------------------------------------
  const budget = useMemo(() => {
    const byKey: Record<string, unknown> = {}
    data.appConfig.forEach((e) => {
      byKey[e.app_config__config_key] = e.app_config__value_json
    })
    return byKey
  }, [data.appConfig])

  const dailyCap = Number(budget.max_cost_per_day_usd ?? 0)
  const spentToday = Number(usage.totals.prog__cost_usd_today ?? 0)
  const capPct = dailyCap > 0 ? spentToday / dailyCap : 0

  const saveBudget = (configKey: string, value: boolean | number | string | string[], failure: string): void => {
    run(() => updateInternalAgentsBudget(token(), configKey, value), failure, true)
  }
  const saveLpStatusCadence = (statusId: string, fields: LpStatusCadenceFields): void => {
    run(() => updateLpStatusCadence(token(), statusId, fields), 'Failed to update the LP status cadence', true)
  }

  // ---- decision-status options (cfg.pipeline_statuses via the synthesized
  // lookup groups) — the follower exclusion criteria and the closed-execution
  // set store status IDS, so the pickers offer the canonical lists. ----------
  const { getLookupOptions } = useLookups()
  const originationStatusOptions = useMemo(
    () => getLookupOptions('Deal Origination Status'),
    [getLookupOptions]
  )
  const executionStatusOptions = useMemo(
    () => getLookupOptions('Deal Execution Status'),
    [getLookupOptions]
  )
  const fundraisingStatusOptions = useMemo(
    () => getLookupOptions('Fundraising Status'),
    [getLookupOptions]
  )
  // All three groups: the label-era matching was category-blind, so the
  // converted id sets legitimately hold e.g. the fundraising 'Declined' —
  // omitting a group would render those entries as raw uuids.
  const dealStatusOptions = useMemo(
    () => [
      { label: 'Deal Origination', options: originationStatusOptions },
      { label: 'Deal Execution', options: executionStatusOptions },
      { label: 'Fundraising', options: fundraisingStatusOptions }
    ],
    [originationStatusOptions, executionStatusOptions, fundraisingStatusOptions]
  )
  const closedExecutionIds = useMemo(
    () =>
      Array.isArray(budget.execution_closed_decision_statuses)
        ? (budget.execution_closed_decision_statuses as string[])
        : [],
    [budget]
  )

  // ---- agent types --------------------------------------------------------
  const agentTypeColumns = [
    {
      title: 'Agent type',
      dataIndex: 'internal_agent_types__name',
      render: (v: string, row: InternalAgentTypeAdminRow) => (
        <div>
          <div>{v || row.internal_agent_types__agent_type_key}</div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            {row.internal_agent_types__agent_type_key} · {row.prog__instance_count ?? 0} instances
          </Text>
        </div>
      )
    },
    {
      title: (
        <Tooltip title='A single run stops calling the model once it has spent this much. Checked before every call.'>
          Max cost / run
        </Tooltip>
      ),
      dataIndex: 'internal_agent_types__max_cost_per_run_usd',
      width: 130,
      render: (v: number, row: InternalAgentTypeAdminRow) => (
        <span className='flex items-center gap-1'>
          <Text type='secondary'>$</Text>
          <InlineNumberField
            value={v ?? null}
            min={0.01}
            max={1000}
            width={90}
            onCommit={(next) =>
              run(
                () =>
                  updateInternalAgentType(token(), row.internal_agent_types__id, {
                    max_cost_per_run_usd: next
                  }),
                'Failed to update the per-run cost cap'
              )
            }
          />
        </span>
      )
    },
    {
      title: (
        <Tooltip title='Runs allowed per instance per day. Queued runs beyond it are pushed to the next UTC day rather than failed.'>
          Runs / day
        </Tooltip>
      ),
      dataIndex: 'internal_agent_types__max_runs_per_day',
      width: 110,
      render: (v: number, row: InternalAgentTypeAdminRow) => (
        <InlineNumberField
          value={v ?? null}
          min={1}
          max={10000}
          width={90}
          onCommit={(next) =>
            run(
              () =>
                updateInternalAgentType(token(), row.internal_agent_types__id, {
                  max_runs_per_day: next
                }),
              'Failed to update the daily run cap'
            )
          }
        />
      )
    },
    {
      title: (
        <Tooltip title='How much of the context window the agent’s accumulated memories may occupy.'>
          Memory tokens
        </Tooltip>
      ),
      dataIndex: 'internal_agent_types__memory_token_budget',
      width: 130,
      render: (v: number, row: InternalAgentTypeAdminRow) => (
        <InlineNumberField
          value={v ?? null}
          min={256}
          max={200000}
          width={100}
          onCommit={(next) =>
            run(
              () =>
                updateInternalAgentType(token(), row.internal_agent_types__id, {
                  memory_token_budget: next
                }),
              'Failed to update the memory budget'
            )
          }
        />
      )
    },
    {
      title: (
        <Tooltip title='When a company gains this type’s auto-provision role, queue its backfill straight away instead of waiting for an operator batch. The number caps how many auto-backfills start per hour — a bulk holdings import can flip hundreds of roles in one transaction. 0 = uncapped. Over the cap the follower is still created and still shows as eligible for the operator batch.'>
          Auto-backfill
        </Tooltip>
      ),
      dataIndex: 'internal_agent_types__auto_backfill_on_provision',
      width: 160,
      render: (v: boolean, row: InternalAgentTypeAdminRow) => {
        // Inert without a role to provision on, so it reads as not applicable
        // rather than as a control that does nothing.
        if (!row.internal_agent_types__auto_provision_role) {
          return <Text type='secondary'>—</Text>
        }
        const on = v !== false
        return (
          <span className='flex items-center gap-2'>
            <Switch
              size='small'
              checked={on}
              onChange={(checked) =>
                run(
                  () =>
                    updateInternalAgentType(token(), row.internal_agent_types__id, {
                      auto_backfill_on_provision: checked
                    }),
                  'Failed to update auto-backfill',
                  true
                )
              }
            />
            {on && (
              <span className='flex items-center gap-1'>
                <InlineNumberField
                  value={row.internal_agent_types__auto_backfill_max_per_hour ?? null}
                  min={0}
                  max={1000}
                  width={64}
                  onCommit={(next) =>
                    run(
                      () =>
                        updateInternalAgentType(token(), row.internal_agent_types__id, {
                          auto_backfill_max_per_hour: next
                        }),
                      'Failed to update the auto-backfill cap'
                    )
                  }
                />
                <Text type='secondary'>/hr</Text>
              </span>
            )}
          </span>
        )
      }
    },
    {
      title: (
        <Tooltip title='How long the follower keeps reading after the company loses the auto-provision role — an exit is followed by closing statements, final distributions and tax paperwork for months. When it elapses the follower is paused: nothing further is written, and its memories, brief, states, KPIs and run history are all kept. Empty = follow forever. 0 = stop at the next hourly sweep.'>
          Unfollow after (days)
        </Tooltip>
      ),
      dataIndex: 'internal_agent_types__unfollow_lag_days',
      width: 160,
      render: (v: number, row: InternalAgentTypeAdminRow) =>
        row.internal_agent_types__auto_provision_role ? (
          <InlineNumberField
            value={v ?? null}
            min={0}
            max={3650}
            width={80}
            placeholder='Never'
            onCommit={(next) =>
              run(
                () =>
                  // An explicit null is meaningful here — it clears the lag to
                  // "follow forever" — so the key always goes on the wire.
                  updateInternalAgentType(token(), row.internal_agent_types__id, {
                    unfollow_lag_days: next
                  }),
                'Failed to update the unfollow lag'
              )
            }
          />
        ) : (
          <Text type='secondary'>—</Text>
        )
    },
    {
      title: (
        <Tooltip title='How long an interaction-triggered run waits for the burst to settle before it is due. One run then reads the whole burst (email thread, meeting plus follow-ups) instead of one run per item; each further interaction pushes the due time out again, up to four debounces from the first. A single quiet interaction waits the full value — 5–10 keeps most bursts merged while feeling responsive. Empty = 30.'>
          Debounce (min)
        </Tooltip>
      ),
      dataIndex: 'internal_agent_types__debounce_minutes',
      width: 120,
      render: (v: number | null | undefined, row: InternalAgentTypeAdminRow) => (
        <InlineNumberField
          value={v ?? null}
          min={1}
          max={1440}
          width={80}
          placeholder='30'
          onCommit={(next) =>
            // No clear here: NULL is not a stored meaning (the enqueue falls
            // back to 30), so an emptied field is a no-op.
            next == null
              ? Promise.resolve()
              : run(
                  () =>
                    updateInternalAgentType(token(), row.internal_agent_types__id, {
                      debounce_minutes: next
                    }),
                  'Failed to update the debounce'
                )
          }
        />
      )
    },
    {
      title: (
        <Tooltip title='How far back a follower reads the first time it runs (the backfill depth policy). A subject averaging at least the threshold interactions per month over the last year is “frequent” and gets the frequent lookback; the rest get the sparse one (empty = unbounded). Inside that lookback only the newest N interactions are read — older ones stay behind the floor, where Follower Agents → Deepen can reach them later. Raising N raises the first-run cost: at 40 interactions a run, 1000 is 25 runs, spread over days by the daily run cap.'>
          Backfill depth
        </Tooltip>
      ),
      dataIndex: 'internal_agent_types__backfill_max_interactions',
      width: 400,
      render: (_: unknown, row: InternalAgentTypeAdminRow) => {
        const depth = (fields: InternalAgentTypeFields, failure: string) =>
          run(() => updateInternalAgentType(token(), row.internal_agent_types__id, fields), failure)
        return (
          <span className='flex items-center gap-1 whitespace-nowrap'>
            <Text type='secondary'>newest</Text>
            <InlineNumberField
              value={row.internal_agent_types__backfill_max_interactions ?? null}
              min={1}
              max={20000}
              width={64}
              onCommit={(next) =>
                next == null
                  ? Promise.resolve()
                  : depth({ backfill_max_interactions: next }, 'Failed to update the backfill cap')
              }
            />
            <Text type='secondary'>in</Text>
            <InlineNumberField
              value={row.internal_agent_types__backfill_lookback_months_frequent ?? null}
              min={1}
              max={240}
              width={48}
              onCommit={(next) =>
                next == null
                  ? Promise.resolve()
                  : depth({ backfill_lookback_months_frequent: next }, 'Failed to update the frequent lookback')
              }
            />
            <Text type='secondary'>mo if ≥</Text>
            <InlineNumberField
              value={row.internal_agent_types__backfill_frequency_threshold_per_month ?? null}
              min={0}
              max={1000}
              width={48}
              onCommit={(next) =>
                next == null
                  ? Promise.resolve()
                  : depth({ backfill_frequency_threshold_per_month: next }, 'Failed to update the frequency threshold')
              }
            />
            <Text type='secondary'>/mo, else</Text>
            <InlineNumberField
              value={row.internal_agent_types__backfill_lookback_months_sparse ?? null}
              min={1}
              max={240}
              width={48}
              placeholder='∞'
              onCommit={(next) =>
                // An explicit null is meaningful here — it clears the sparse
                // lookback to "unbounded" — so the key always goes on the wire.
                depth({ backfill_lookback_months_sparse: next }, 'Failed to update the sparse lookback')
              }
            />
            <Text type='secondary'>mo</Text>
          </span>
        )
      }
    },
    {
      title: (
        <Tooltip title="Follower qualification criteria: a deal parked at a stage carrying one of these decision statuses does not count as pipeline membership, so the follower skips (or unfollows) it. Empty = no filter.">
          Skip when status is
        </Tooltip>
      ),
      dataIndex: 'internal_agent_types__follow_exclude_decision_statuses',
      width: 260,
      render: (v: string[] | null | undefined, row: InternalAgentTypeAdminRow) =>
        row.internal_agent_types__subject_table === 'deals' ? (
          <Select
            mode='multiple'
            size='small'
            style={{ width: 240 }}
            maxTagCount={2}
            placeholder='No exclusions'
            value={v ?? []}
            options={dealStatusOptions}
            optionFilterProp='label'
            onChange={(vals: string[]) =>
              run(
                () =>
                  // An explicit null clears the filter — the key's presence is
                  // what tells the backend to clear vs. leave unchanged.
                  updateInternalAgentType(token(), row.internal_agent_types__id, {
                    follow_exclude_decision_statuses: vals.length ? vals : null
                  }),
                'Failed to update the excluded statuses',
                true
              )
            }
          />
        ) : (
          <Text type='secondary'>—</Text>
        )
    },
    {
      title: (
        <Tooltip title='How often a scheduled run is queued for every instance of this type. None = this type only runs on interactions or on demand. The interval multiplies the unit — 2 weekly is fortnightly.'>
          Schedule
        </Tooltip>
      ),
      dataIndex: 'internal_agent_types__schedule_frequency_unit',
      width: 200,
      render: (v: string | null | undefined, row: InternalAgentTypeAdminRow) => {
        const unit = v || 'none'
        return (
          <span className='flex items-center gap-1'>
            <Select
              size='small'
              style={{ width: 96 }}
              value={unit}
              options={SCHEDULE_UNIT_OPTIONS}
              onChange={(next: string) =>
                run(
                  () =>
                    updateInternalAgentType(token(), row.internal_agent_types__id, {
                      schedule_frequency_unit: next as 'none' | 'daily' | 'weekly' | 'monthly'
                    }),
                  'Failed to update the schedule',
                  true
                )
              }
            />
            {unit !== 'none' && (
              <>
                <Text type='secondary'>every</Text>
                <InlineNumberField
                  value={row.internal_agent_types__schedule_interval_count ?? null}
                  min={1}
                  max={365}
                  width={56}
                  onCommit={(next) =>
                    run(
                      () =>
                        updateInternalAgentType(token(), row.internal_agent_types__id, {
                          schedule_interval_count: next ?? undefined
                        }),
                      'Failed to update the schedule interval'
                    )
                  }
                />
                {/* Which run_mode the tick queues. Read-only — it must name a
                    mode this type's handler implements — but without it the
                    schedule says when something fires and never what. */}
                {row.internal_agent_types__scheduled_run_mode && (
                  <Tooltip title='The run mode a scheduled tick queues for this type.'>
                    <Tag>{row.internal_agent_types__scheduled_run_mode}</Tag>
                  </Tooltip>
                )}
              </>
            )}
          </span>
        )
      }
    },
    {
      title: (
        <Tooltip title='Which day the scheduled run is queued. Weekly schedules fire on a WEEKDAY, monthly ones on a day of the month — the stored anchor is a date, but only the part shown here is read. Empty falls back to the day this agent type was created, which is rarely the day you want.'>
          Runs on
        </Tooltip>
      ),
      dataIndex: 'internal_agent_types__schedule_anchor_date',
      width: 210,
      render: (v: string | null | undefined, row: InternalAgentTypeAdminRow) => {
        const unit = row.internal_agent_types__schedule_frequency_unit || 'none'
        const interval = row.internal_agent_types__schedule_interval_count ?? 1
        if (unit === 'none') return <Text type='secondary'>—</Text>

        // Every branch writes a DATE, because that is what enqueue_due reads —
        // the control just stops asking for the parts of it that are ignored.
        const save = (anchor: string | null) =>
          run(
            () =>
              // An explicit null is meaningful (clears back to the date_created
              // fallback), so the key always goes on the wire.
              updateInternalAgentType(token(), row.internal_agent_types__id, {
                schedule_anchor_date: anchor
              }),
            'Failed to update the schedule day',
            true
          )

        const hint = nextRunHint(unit, interval, v)
        const nextLabel = hint ? (
          <Text type='secondary' style={{ fontSize: 12 }}>
            next {hint}
          </Text>
        ) : null

        if (unit === 'weekly') {
          return (
            <span className='flex items-center gap-2'>
              <Select
                size='small'
                style={{ width: 108 }}
                allowClear
                placeholder='Created day'
                value={v ? dayjs(v).day() : undefined}
                options={WEEKDAY_OPTIONS}
                onChange={(day: number | undefined) =>
                  save(day === undefined ? null : mostRecentWeekday(day))
                }
              />
              {nextLabel}
            </span>
          )
        }

        if (unit === 'monthly') {
          return (
            <span className='flex items-center gap-2'>
              <InlineNumberField
                value={v ? dayjs(v).date() : null}
                min={1}
                max={31}
                width={64}
                placeholder='Day'
                onCommit={(day) => save(day == null ? null : mostRecentDayOfMonth(day))}
              />
              {nextLabel}
            </span>
          )
        }

        // Daily. At interval 1 every day is due, so the anchor is genuinely
        // inert; above 1 its phase decides which days, and only a full date can
        // express that.
        if (interval <= 1) return <Text type='secondary'>every day</Text>
        return (
          <span className='flex items-center gap-2'>
            <DatePicker
              size='small'
              style={{ width: 120 }}
              value={v ? dayjs(v) : null}
              format='MM/DD/YYYY'
              placeholder='Created date'
              disabledDate={(d) => !!d && d.isAfter(dayjs(), 'day')}
              onChange={(next) => save(next ? next.format('YYYY-MM-DD') : null)}
            />
            {nextLabel}
          </span>
        )
      }
    },
    {
      title: (
        <Tooltip title='Off = no run of this type is queued or executed. The other types keep running.'>
          Enabled
        </Tooltip>
      ),
      dataIndex: 'internal_agent_types__enabled',
      width: 90,
      render: (v: boolean, row: InternalAgentTypeAdminRow) => (
        <Switch
          size='small'
          checked={v !== false}
          onChange={(checked) =>
            run(
              () =>
                updateInternalAgentType(token(), row.internal_agent_types__id, { enabled: checked }),
              'Failed to update the agent type',
              true
            )
          }
        />
      )
    }
  ]

  // ---- task routing -------------------------------------------------------
  const modelOptions = useMemo(
    () =>
      data.models
        .filter((m) => m.llm_models__enabled !== false && m.llm_models__is_effective !== false)
        .map((m) => ({
          value: m.llm_models__model_key,
          label: `${m.llm_models__model_key} · ${m.llm_models__model_name}`
        })),
    [data.models]
  )

  const openParams = (row: InternalAgentTaskModelAdminRow): void => setParamsRow(row)

  const saveParams = async (next: Record<string, unknown>): Promise<void> => {
    if (!paramsRow) return
    setSaving(true)
    const ok = await run(
      () =>
        updateInternalAgentTaskModel(token(), paramsRow.internal_agent_task_models__id, {
          params: next
        }),
      'Failed to save the task options'
    )
    setSaving(false)
    if (ok) setParamsRow(null)
  }

  const taskModelColumns = [
    {
      title: 'Agent type',
      dataIndex: 'internal_agent_types__agent_type_key',
      width: 170,
      render: (v: string) => <Text>{v}</Text>
    },
    { title: 'Task', dataIndex: 'internal_agent_task_models__task_key', width: 150 },
    {
      title: 'Model',
      dataIndex: 'internal_agent_task_models__llm_model_key',
      width: 260,
      render: (v: string, row: InternalAgentTaskModelAdminRow) => (
        <span className='flex items-center gap-2'>
          <Select
            size='small'
            className='min-w-[190px]'
            value={v}
            options={modelOptions}
            onChange={(next) =>
              run(
                () =>
                  updateInternalAgentTaskModel(token(), row.internal_agent_task_models__id, {
                    llm_model_key: next
                  }),
                'Failed to change the model',
                true
              )
            }
          />
          {row.prog__model_is_live === false ? (
            <Tooltip title='This model is missing or disabled, so runs for this task park as unconfigured.'>
              <Tag color='red'>Unresolved</Tag>
            </Tooltip>
          ) : null}
        </span>
      )
    },
    {
      title: 'Options',
      key: 'params',
      width: 240,
      render: (_: unknown, row: InternalAgentTaskModelAdminRow) => (
        <button
          type='button'
          className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
          onClick={() => openParams(row)}
        >
          {bagSummary(row.internal_agent_task_models__params) || 'Model defaults'}
        </button>
      )
    },
    {
      title: (
        <Tooltip title='Off = this task has no model, so its runs park as unconfigured rather than falling back.'>
          Enabled
        </Tooltip>
      ),
      dataIndex: 'internal_agent_task_models__enabled',
      width: 90,
      render: (v: boolean, row: InternalAgentTaskModelAdminRow) => (
        <Switch
          size='small'
          checked={v !== false}
          onChange={(checked) =>
            run(
              () =>
                updateInternalAgentTaskModel(token(), row.internal_agent_task_models__id, {
                  enabled: checked
                }),
              'Failed to update the task routing',
              true
            )
          }
        />
      )
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: unknown, row: InternalAgentTaskModelAdminRow) => (
        <Popconfirm
          title='Remove this task routing?'
          description='Runs for this task will park as unconfigured until a model is routed again.'
          okText='Remove'
          okButtonProps={{ danger: true }}
          onConfirm={() =>
            run(
              () => deleteInternalAgentTaskModel(token(), row.internal_agent_task_models__id),
              'Failed to remove the task routing'
            )
          }
        >
          <Button size='small' type='text' danger icon={<DeleteOutlined />} />
        </Popconfirm>
      )
    }
  ]

  const handleAddRouting = async (): Promise<void> => {
    if (!newRouting?.agent_type_id || !newRouting.task_key || !newRouting.llm_model_key) return
    setSaving(true)
    const ok = await run(
      () => createInternalAgentTaskModel(token(), newRouting),
      'Failed to add the task routing'
    )
    setSaving(false)
    if (ok) setNewRouting(null)
  }

  // ---- model registry -----------------------------------------------------
  const openNewModel = (): void => {
    setModelDraftId(null)
    setModelDraft({
      provider: data.providers[0] || 'anthropic',
      api_key_secret_name: 'ANTHROPIC_API_KEY',
      max_output_tokens_default: 8192,
      enabled: true
    })
  }

  const openEditModel = (row: LlmModelAdminRow): void => {
    setModelDraftId(row.llm_models__id)
    setModelDraft({
      model_key: row.llm_models__model_key,
      provider: row.llm_models__provider,
      model_name: row.llm_models__model_name,
      api_key_secret_name: row.llm_models__api_key_secret_name,
      input_cost_per_mtok: row.llm_models__input_cost_per_mtok ?? 0,
      output_cost_per_mtok: row.llm_models__output_cost_per_mtok ?? 0,
      max_output_tokens_default: row.llm_models__max_output_tokens_default ?? 8192,
      description: row.llm_models__description ?? ''
    })
  }

  const handleSaveModel = async (): Promise<void> => {
    if (!modelDraft) return
    setSaving(true)
    const ok = await run(
      () =>
        modelDraftId
          ? updateLlmModel(token(), modelDraftId, modelDraft)
          : createLlmModel(token(), modelDraft),
      'Failed to save the model'
    )
    setSaving(false)
    if (ok) {
      setModelDraft(null)
      setModelDraftId(null)
    }
  }

  const modelColumns = [
    {
      title: 'Model',
      dataIndex: 'llm_models__model_key',
      render: (v: string, row: LlmModelAdminRow) => (
        <div>
          <button
            type='button'
            className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
            onClick={() => openEditModel(row)}
          >
            {v}
          </button>
          <div>
            <Text type='secondary' style={{ fontSize: 12 }}>
              {row.llm_models__model_name}
            </Text>
          </div>
        </div>
      )
    },
    {
      title: 'Provider',
      dataIndex: 'llm_models__provider',
      width: 110,
      render: (v: string) => <Tag>{v}</Tag>
    },
    {
      title: (
        <Tooltip title='USD per million tokens. These are what the spend ledger multiplies, so a stale price makes every budget cap wrong.'>
          Cost / Mtok
        </Tooltip>
      ),
      key: 'cost',
      width: 150,
      render: (_: unknown, row: LlmModelAdminRow) => (
        <Text type='secondary'>
          {money(row.llm_models__input_cost_per_mtok)} in ·{' '}
          {money(row.llm_models__output_cost_per_mtok)} out
        </Text>
      )
    },
    {
      title: 'Max output',
      dataIndex: 'llm_models__max_output_tokens_default',
      width: 110,
      render: (v: number) => <Text type='secondary'>{compactNumber(v)}</Text>
    },
    {
      title: (
        <Tooltip title='How many agent tasks currently route to this model. A model in use cannot be disabled or deleted.'>
          In use
        </Tooltip>
      ),
      dataIndex: 'prog__task_model_count',
      width: 90,
      render: (v: number) =>
        v > 0 ? <Tag color='blue'>{v} task{v === 1 ? '' : 's'}</Tag> : <Text type='secondary'>—</Text>
    },
    {
      title: 'Scope',
      key: 'scope',
      width: 130,
      render: (_: unknown, row: LlmModelAdminRow) =>
        row.llm_models__is_effective === false ? (
          <Tooltip title={`Shadowed by the ${data.resolvedEnvironment} row for this model key, so it has no effect here.`}>
            <Tag>Shadowed</Tag>
          </Tooltip>
        ) : row.llm_models__is_environment_override ? (
          <Tag color='purple'>{row.llm_models__environment}</Tag>
        ) : (
          <Text type='secondary'>All environments</Text>
        )
    },
    {
      title: 'Enabled',
      dataIndex: 'llm_models__enabled',
      width: 90,
      render: (v: boolean, row: LlmModelAdminRow) => (
        <Switch
          size='small'
          checked={v !== false}
          onChange={(checked) =>
            run(
              () => updateLlmModel(token(), row.llm_models__id, { enabled: checked }),
              'Failed to update the model',
              true
            )
          }
        />
      )
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: unknown, row: LlmModelAdminRow) => (
        <Popconfirm
          title='Delete this model?'
          description='Only possible while no agent task still routes to it.'
          okText='Delete'
          okButtonProps={{ danger: true }}
          onConfirm={() =>
            run(() => deleteLlmModel(token(), row.llm_models__id), 'Failed to delete the model', true)
          }
        >
          <Button size='small' type='text' danger icon={<DeleteOutlined />} />
        </Popconfirm>
      )
    }
  ]

  // ---- usage --------------------------------------------------------------
  const usageColumns = [
    { title: 'Model', dataIndex: 'agent_run_llm_calls__llm_model_key' },
    {
      title: 'Provider',
      dataIndex: 'agent_run_llm_calls__provider',
      width: 110,
      render: (v: string) => <Text type='secondary'>{v}</Text>
    },
    {
      title: 'Spend',
      dataIndex: 'prog__cost_usd_today',
      width: 110,
      render: (v: number) => <Text>{money(v)}</Text>
    },
    {
      title: 'Calls',
      dataIndex: 'prog__call_count_today',
      width: 90,
      render: (v: number) => <Text type='secondary'>{compactNumber(v)}</Text>
    },
    {
      title: 'Tokens in / out',
      key: 'tokens',
      width: 190,
      render: (_: unknown, row: { prog__input_tokens_today?: number; prog__output_tokens_today?: number }) => (
        <Text type='secondary'>
          {compactNumber(row.prog__input_tokens_today)} / {compactNumber(row.prog__output_tokens_today)}
        </Text>
      )
    }
  ]

  const usageByTypeColumns = [
    { title: 'Agent type', dataIndex: 'internal_agent_types__name' },
    {
      title: 'Spend',
      dataIndex: 'prog__cost_usd_today',
      width: 110,
      render: (v: number) => <Text>{money(v)}</Text>
    },
    {
      title: 'Runs',
      key: 'runs',
      width: 130,
      render: (_: unknown, row: { prog__runs_today?: number; internal_agent_types__max_runs_per_day?: number }) => (
        <Text type='secondary'>
          {compactNumber(row.prog__runs_today)}
          {row.internal_agent_types__max_runs_per_day
            ? ` · cap ${compactNumber(row.internal_agent_types__max_runs_per_day)}/instance`
            : ''}
        </Text>
      )
    },
    {
      title: 'Calls',
      dataIndex: 'prog__llm_calls_today',
      width: 90,
      render: (v: number) => <Text type='secondary'>{compactNumber(v)}</Text>
    }
  ]

  const usageByRunModeColumns = [
    { title: 'Run mode', dataIndex: 'agent_runs__run_mode' },
    { title: 'Spend', dataIndex: 'prog__cost_usd', width: 110, render: (v: number) => <Text>{money(v)}</Text> },
    {
      title: 'Runs', key: 'runs', width: 130,
      render: (_: unknown, row: { prog__runs?: number; prog__runs_failed?: number }) => (
        <Text type='secondary'>
          {compactNumber(row.prog__runs)}
          {row.prog__runs_failed ? ` · ${compactNumber(row.prog__runs_failed)} failed` : ''}
        </Text>
      )
    },
    { title: 'Calls', dataIndex: 'prog__llm_calls', width: 90, render: (v: number) => <Text type='secondary'>{compactNumber(v)}</Text> }
  ]

  const usageBySubjectColumns = [
    {
      title: 'Top subjects by spend', key: 'subject',
      render: (_: unknown, row: { subject_name?: string | null; agent_instances__subject_table?: string; internal_agent_types__agent_type_key?: string }) => (
        <span>
          {row.subject_name || <Text type='secondary'>{row.agent_instances__subject_table}</Text>}
          <Text type='secondary' style={{ fontSize: 11, marginLeft: 6 }}>{row.internal_agent_types__agent_type_key}</Text>
        </span>
      )
    },
    { title: 'Spend', dataIndex: 'prog__cost_usd', width: 110, render: (v: number) => <Text>{money(v)}</Text> },
    { title: 'Runs', dataIndex: 'prog__runs', width: 90, render: (v: number) => <Text type='secondary'>{compactNumber(v)}</Text> },
    { title: 'Calls', dataIndex: 'prog__llm_calls', width: 90, render: (v: number) => <Text type='secondary'>{compactNumber(v)}</Text> }
  ]

  const usageByDayColumns = [
    { title: 'Day (UTC)', dataIndex: 'prog__day', render: (v: string) => dayjs(v).format('MM/DD/YYYY') },
    { title: 'Spend', dataIndex: 'prog__cost_usd', width: 110, render: (v: number) => <Text>{money(v)}</Text> },
    { title: 'Runs', dataIndex: 'prog__runs', width: 90, render: (v: number) => <Text type='secondary'>{compactNumber(v)}</Text> },
    { title: 'Calls', dataIndex: 'prog__llm_calls', width: 90, render: (v: number) => <Text type='secondary'>{compactNumber(v)}</Text> }
  ]

  const paramSpecs = paramFieldSpecs(data.taskParamKeys, paramsRow?.prog__model_provider)

  return (
    <div className={`${CONFIG_PANEL_SCROLL_CLASS} space-y-4`}>
      <FormSection
        title='Global budget'
        titleColor='blue'
        scrollable={false}
        loading={loading}
        headerActions={
          <span className='flex items-center gap-2'>
            <Text type='secondary' style={{ fontSize: 12 }}>
              {money(spentToday)} of {money(dailyCap)} spent today
            </Text>
            {dailyCap > 0 && capPct >= 1 ? (
              <Tag color='red'>Daily cap reached</Tag>
            ) : dailyCap > 0 && capPct >= 0.8 ? (
              <Tag color='gold'>{Math.round(capPct * 100)}% of cap</Tag>
            ) : null}
            <Button size='small' icon={<ReloadOutlined />} onClick={load}>
              Refresh
            </Button>
          </span>
        }
      >
        <div className='flex flex-col gap-3 max-w-[560px]'>
          <div className='flex items-center justify-between'>
            <Text>Internal agents enabled</Text>
            <Switch
              size='small'
              checked={budget.enabled !== false}
              onChange={(checked) =>
                saveBudget('enabled', checked, 'Failed to update the master switch')
              }
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Off = every run request is refused. Queued work waits rather than failing.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Max cost per day ($)</Text>
            <InlineNumberField
              value={(budget.max_cost_per_day_usd as number) ?? null}
              min={0.01}
              max={10000}
              width={120}
              onCommit={(v) =>
                v !== null && saveBudget('max_cost_per_day_usd', v, 'Failed to update the daily cost cap')
              }
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            One shared pool across every agent type, summed since 00:00 UTC. Runs stop calling the
            model once it is reached.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Model call timeout (seconds)</Text>
            <InlineNumberField
              value={(budget.llm_timeout_seconds as number) ?? null}
              min={10}
              max={900}
              width={120}
              onCommit={(v) =>
                v !== null && saveBudget('llm_timeout_seconds', v, 'Failed to update the timeout')
              }
            />
          </div>

          <div className='flex items-center justify-between'>
            <Text>Max concurrent runs</Text>
            <InlineNumberField
              value={(budget.max_concurrent_runs as number) ?? null}
              min={1}
              max={32}
              width={120}
              onCommit={(v) =>
                v !== null && saveBudget('max_concurrent_runs', v, 'Failed to update the concurrency')
              }
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Applies from the next batch the queue dispatches; runs already in flight finish under the
            old limit. Counted per backend replica.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Closed execution statuses</Text>
            <Select
              mode='multiple'
              size='small'
              style={{ width: 280 }}
              maxTagCount={2}
              placeholder='Select statuses'
              value={closedExecutionIds}
              options={dealStatusOptions}
              optionFilterProp='label'
              onChange={(vals: string[]) =>
                saveBudget(
                  'execution_closed_decision_statuses',
                  vals,
                  'Failed to update the closed execution statuses'
                )
              }
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            A deal whose execution stage carries one of these decision statuses counts as a CLOSED
            closing: inbound mail stops linking to it and the execution follower stops waking for it.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Send to Outlook (agent drafts)</Text>
            <Switch
              size='small'
              data-testid='outlook-drafts-enabled'
              checked={budget.outlook_drafts_enabled === true}
              onChange={(checked) =>
                saveBudget('outlook_drafts_enabled', checked, 'Failed to update Send to Outlook')
              }
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Global switch for My Drafts → Send to Outlook, which creates an agent draft in the
            accepting user&apos;s own Outlook Drafts folder. Each mailbox is also switched
            individually under Interaction Tracking. Off = every card shows Copy only. Reservoir
            never sends.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Append the user&apos;s signature</Text>
            <Switch
              size='small'
              data-testid='outlook-drafts-append-signature'
              checked={budget.outlook_drafts_append_signature !== false}
              onChange={(checked) =>
                saveBudget('outlook_drafts_append_signature', checked, 'Failed to update the signature setting')
              }
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Outlook adds no signature to a draft created this way, so Reservoir appends the
            user&apos;s own (Settings → My signature, else the firm default).
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Sent-match window (days)</Text>
            <InlineNumberField
              value={(budget.outlook_sent_match_window_days as number) ?? null}
              min={1}
              max={20}
              width={120}
              onCommit={(v) =>
                v !== null && saveBudget('outlook_sent_match_window_days', v, 'Failed to update the sent-match window')
              }
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            After Send to Outlook, how long the sweep looks for the user&apos;s outbound email to the
            account when the text was copied into another mail. Capped at 20 days by the inbound
            mail purge.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Sent-match similarity threshold (0–1)</Text>
            <InlineNumberField
              value={(budget.outlook_sent_similarity_threshold as number) ?? null}
              min={0.05}
              max={1}
              width={120}
              onCommit={(v) =>
                v !== null && saveBudget('outlook_sent_similarity_threshold', v, 'Failed to update the similarity threshold')
              }
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            How much of the draft must appear in a later email for it to be offered as
            &quot;Looks like you sent this&quot;. The user always confirms — nothing links on its own.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Outlook reconcile interval (minutes)</Text>
            <InlineNumberField
              value={(budget.outlook_reconcile_interval_minutes as number) ?? null}
              min={5}
              max={1440}
              width={120}
              onCommit={(v) =>
                v !== null && saveBudget('outlook_reconcile_interval_minutes', v, 'Failed to update the reconcile interval')
              }
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            How often a draft still sitting in Outlook is re-read (exists / draft / sent). A read only —
            Reservoir never edits or deletes the item.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Expire unsent Outlook drafts after (days)</Text>
            <InlineNumberField
              value={(budget.exported_draft_expiry_days as number) ?? null}
              min={1}
              max={365}
              width={120}
              onCommit={(v) =>
                v !== null && saveBudget('exported_draft_expiry_days', v, 'Failed to update the exported draft expiry')
              }
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            A draft in Outlook with no linked send for this long stops waiting in Reservoir. The
            Outlook item is untouched.
          </Text>
        </div>
      </FormSection>

      {/* Cadence (LP agents UAT follow-ups PR B + PR C). WHO gets chased and
          drafted is decided per LP status (cfg.comms_lp_statuses: threshold,
          draft mode, scheduler priority); WHEN by the thresholds below; HOW
          MANY by the per-RM weekly budget. Everything here is read live by
          the deterministic cadence gate before any model call, so a change
          applies to the next cadence review with no restart. */}
      <FormSection title='Cadence' titleColor='blue' scrollable={false} loading={loading}>
        <div className='flex flex-col gap-2'>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Who the LP relationship manager chases, and when a due relationship becomes a draft
            rather than a chip. Read live by the weekly cadence review and by Run now → Cadence
            review on Follower Agents; no restart needed. A quiet relationship inside every
            threshold costs nothing: the review skips before any model call. A reply owed on an
            unanswered inbound is never rationed by any of this.
          </Text>

          <Table
            size='small'
            pagination={false}
            rowKey='comms_lp_statuses__id'
            dataSource={data.lpStatusCadence}
            columns={[
              {
                title: 'LP status',
                dataIndex: 'comms_lp_statuses__label',
                width: 140,
                render: (label: string) => <Text strong>{label}</Text>
              },
              {
                title: (
                  <Tooltip title="Days since the firm's last outbound contact, meeting or reply before the relationship manager raises touchpoint-due for an LP of this status. Never = this status is not chased by cadence at all (a reply owed still is). An LP with an open fundraising prospect is the prospect tracker's to chase whatever its status.">
                    Touchpoint due after (days)
                  </Tooltip>
                ),
                dataIndex: 'comms_lp_statuses__rm_touchpoint_due_days',
                width: 260,
                render: (days: number | null | undefined, row: LpStatusCadenceRow) => (
                  <span className='flex items-center gap-2'>
                    <Switch
                      size='small'
                      checked={days != null}
                      checkedChildren='chased'
                      unCheckedChildren='never'
                      onChange={(on) =>
                        saveLpStatusCadence(row.comms_lp_statuses__id, {
                          rm_touchpoint_due_days: on ? 90 : null
                        })
                      }
                    />
                    {days != null ? (
                      <InlineNumberField
                        value={days}
                        min={1}
                        max={730}
                        width={96}
                        onCommit={(v) =>
                          v !== null &&
                          saveLpStatusCadence(row.comms_lp_statuses__id, { rm_touchpoint_due_days: v })
                        }
                      />
                    ) : (
                      <Text type='secondary'>reactive only</Text>
                    )}
                  </span>
                )
              },
              {
                title: (
                  <Tooltip title='Draft = a due touchpoint drafts an outreach (subject to the weekly budget below). Signal = the chip only; the relationship manager is told, nobody is written to.'>
                    When due
                  </Tooltip>
                ),
                dataIndex: 'comms_lp_statuses__rm_cadence_draft_mode',
                width: 170,
                render: (
                  mode: LpStatusCadenceRow['comms_lp_statuses__rm_cadence_draft_mode'],
                  row: LpStatusCadenceRow
                ) => (
                  <Select
                    size='small'
                    style={{ width: 150 }}
                    value={mode}
                    disabled={row.comms_lp_statuses__rm_touchpoint_due_days == null}
                    options={[
                      { value: 'draft', label: 'Draft an outreach' },
                      { value: 'signal', label: 'Signal only' }
                    ]}
                    onChange={(next: 'draft' | 'signal') =>
                      saveLpStatusCadence(row.comms_lp_statuses__id, { rm_cadence_draft_mode: next })
                    }
                  />
                )
              },
              {
                title: (
                  <Tooltip title='Order of the weekly cadence pass across the fleet: lower runs first, so the weekly drafts budget goes to these relationships before the rest. Ties break on the size of closed commitments.'>
                    Priority
                  </Tooltip>
                ),
                dataIndex: 'comms_lp_statuses__agent_priority',
                width: 120,
                render: (priority: number, row: LpStatusCadenceRow) => (
                  <InlineNumberField
                    value={priority}
                    min={1}
                    max={1000}
                    width={80}
                    onCommit={(v) =>
                      v !== null && saveLpStatusCadence(row.comms_lp_statuses__id, { agent_priority: v })
                    }
                  />
                )
              }
            ]}
          />
          <Text type='secondary' style={{ fontSize: 12 }}>
            The status itself is derived (commitments and fundraising prospects decide Active,
            Historic, Target, Declined, None); only its cadence policy is set here. Target LPs are
            chased by the prospect tracker at the prospect thresholds below, never by the
            relationship manager.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Cadence drafts per relationship manager per week</Text>
            <InlineNumberField
              value={(budget.rm_cadence_drafts_per_rm_per_week as number) ?? null}
              min={0}
              max={100}
              width={120}
              onCommit={(v) => v !== null && saveBudget('rm_cadence_drafts_per_rm_per_week', v, 'Failed to update the cadence drafts budget')}
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Outreach drafts one relationship manager receives from the LP relationship manager per rolling 7 days, counted whatever happened to them since. Beyond it a due touchpoint raises the chip only. 0 = never draft on cadence.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Prospect follow-up drafts per person per week</Text>
            <InlineNumberField
              value={(budget.prospect_followups_per_rm_per_week as number) ?? null}
              min={0}
              max={100}
              width={120}
              onCommit={(v) => v !== null && saveBudget('prospect_followups_per_rm_per_week', v, 'Failed to update the prospect follow-ups budget')}
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Follow-up drafts one person (the prospect Lead, else the relationship manager) receives from the prospect tracker per rolling 7 days. 0 = never draft on cadence.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Dismiss snoozes the relationship for (days)</Text>
            <InlineNumberField
              value={(budget.rm_dismiss_snooze_days as number) ?? null}
              min={0}
              max={365}
              width={120}
              onCommit={(v) => v !== null && saveBudget('rm_dismiss_snooze_days', v, 'Failed to update the dismiss snooze')}
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Dismissing an outreach or follow-up draft says not now: that relationship&apos;s touchpoint clock is parked for this long, so the same draft does not come straight back on the next weekly pass. Undo lifts it. 0 = no snooze.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Signal-only until</Text>
            <DatePicker
              size='small'
              allowClear
              value={
                typeof budget.rm_cadence_signal_only_until === 'string' && budget.rm_cadence_signal_only_until
                  ? dayjs(budget.rm_cadence_signal_only_until as string)
                  : null
              }
              onChange={(d) =>
                saveBudget(
                  'rm_cadence_signal_only_until',
                  d ? d.format('YYYY-MM-DD') : '',
                  'Failed to update the signal-only date'
                )
              }
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            While today is on or before this date every cadence draft is downgraded to a chip:
            for a fresh fleet, so relationship managers see what is due before anything is
            written for them. Clear it to draft normally.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Unanswered inbound after (days)</Text>
            <InlineNumberField
              value={(budget.rm_unanswered_inbound_days as number) ?? null}
              min={1}
              max={90}
              width={120}
              onCommit={(v) => v !== null && saveBudget('rm_unanswered_inbound_days', v, 'Failed to update the unanswered-inbound threshold')}
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Days an LP&apos;s inbound email may sit without a reply from the firm before the relationship manager flags it and drafts the reply. A reply owed is never rationed by cadence.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Refresh LP states after (days)</Text>
            <InlineNumberField
              value={(budget.rm_state_refresh_days as number) ?? null}
              min={1}
              max={730}
              width={120}
              onCommit={(v) => v !== null && saveBudget('rm_state_refresh_days', v, 'Failed to update the state refresh interval')}
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Days before a persistent LP state chip (relationship strength, stance, engagement) counts as stale and the cadence review re-judges it even on a quiet relationship.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Prospect follow-up due after (days)</Text>
            <InlineNumberField
              value={(budget.prospect_follow_up_due_days as number) ?? null}
              min={1}
              max={365}
              width={120}
              onCommit={(v) => v !== null && saveBudget('prospect_follow_up_due_days', v, 'Failed to update the prospect follow-up threshold')}
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Days since the firm&apos;s last move on a fundraising prospect before the prospect tracker flags a follow-up and drafts it.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Prospect stalled after (days)</Text>
            <InlineNumberField
              value={(budget.prospect_stalled_days as number) ?? null}
              min={1}
              max={365}
              width={120}
              onCommit={(v) => v !== null && saveBudget('prospect_stalled_days', v, 'Failed to update the prospect stalled threshold')}
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Days with no interaction of any kind and no stage change before a prospect counts as stalled.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Expire proposed drafts after (days)</Text>
            <InlineNumberField
              value={(budget.draft_expiry_days as number) ?? null}
              min={1}
              max={365}
              width={120}
              onCommit={(v) => v !== null && saveBudget('draft_expiry_days', v, 'Failed to update the draft expiry')}
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            A proposed draft nobody has accepted, copied or dismissed for this long is retired so it stops blocking a fresh one for the same relationship.
          </Text>

          <div className='flex items-center justify-between'>
            <Text>Fallback touchpoint threshold (days)</Text>
            <InlineNumberField
              value={(budget.rm_touchpoint_due_days_active as number) ?? null}
              min={1}
              max={730}
              width={120}
              onCommit={(v) => v !== null && saveBudget('rm_touchpoint_due_days_active', v, 'Failed to update the fallback touchpoint threshold')}
            />
          </div>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Used only for an LP whose status cannot be resolved (no status row). Every resolved status uses its own row above.
          </Text>
        </div>
      </FormSection>

      <FormSection title='Agent types' titleColor='green' scrollable={false} loading={loading}>
        <Table
          size='small'
          rowKey='internal_agent_types__id'
          pagination={false}
          dataSource={data.agentTypes}
          columns={agentTypeColumns}
        />
      </FormSection>

      <InvestmentCriteriaSection />

      <FormSection
        title='Task routing'
        titleColor='purple'
        scrollable={false}
        loading={loading}
        headerActions={
          <span className='flex items-center gap-2'>
            <Text type='secondary' style={{ fontSize: 12 }}>
              Cheap models for triage, expensive ones for the deep pass
            </Text>
            <Button
              size='small'
              type='primary'
              icon={<PlusOutlined />}
              onClick={() => setNewRouting({})}
            >
              Add routing
            </Button>
          </span>
        }
      >
        <Table
          size='small'
          rowKey='internal_agent_task_models__id'
          pagination={false}
          dataSource={data.taskModels}
          columns={taskModelColumns}
        />
      </FormSection>

      <FormSection
        title='Model registry'
        titleColor='orange'
        scrollable={false}
        loading={loading}
        headerActions={
          <span className='flex items-center gap-2'>
            <Text type='secondary' style={{ fontSize: 12 }}>
              Prices drive the spend accounting — keep them current
            </Text>
            <Button size='small' type='primary' icon={<PlusOutlined />} onClick={openNewModel}>
              Add model
            </Button>
          </span>
        }
      >
        <Table
          size='small'
          rowKey='llm_models__id'
          pagination={false}
          dataSource={data.models}
          columns={modelColumns}
        />
      </FormSection>

      <FormSection
        title={usageDays === 1 ? 'Usage today' : `Usage · last ${usageDays} days`}
        titleColor='gray'
        scrollable={false}
        loading={loading}
        headerActions={
          <span className='flex items-center gap-3'>
            <Text type='secondary' style={{ fontSize: 12 }}>
              {compactNumber(usage.totals.prog__runs_today)} runs ·{' '}
              {compactNumber(usage.totals.prog__llm_calls_today)} model calls ·{' '}
              {money(usage.totals.prog__cost_usd_today)}{' '}
              {usageDays === 1 ? 'since 00:00 UTC' : `since ${usage.since_utc ? dayjs(usage.since_utc).format('MM/DD/YYYY') : ''} (UTC days)`}
            </Text>
            <Select
              size='small'
              style={{ width: 130 }}
              value={usageDays}
              onChange={(v) => setUsageDays(Number(v))}
              options={[
                { value: 1, label: 'Today' },
                { value: 7, label: 'Last 7 days' },
                { value: 30, label: 'Last 30 days' },
                { value: 90, label: 'Last 90 days' }
              ]}
            />
          </span>
        }
      >
        <div className='space-y-3'>
          <Table
            size='small'
            rowKey='internal_agent_types__agent_type_key'
            pagination={false}
            dataSource={usage.by_agent_type}
            columns={usageByTypeColumns}
          />
          <Table
            size='small'
            rowKey='agent_run_llm_calls__llm_model_key'
            pagination={false}
            dataSource={usage.by_model}
            columns={usageColumns}
            locale={{ emptyText: usageDays === 1 ? 'No model calls yet today' : 'No model calls in this window' }}
          />
          <Table
            size='small'
            rowKey='agent_runs__run_mode'
            pagination={false}
            dataSource={usage.by_run_mode ?? []}
            columns={usageByRunModeColumns}
            locale={{ emptyText: 'No runs in this window' }}
          />
          <Table
            size='small'
            rowKey='agent_instances__id'
            pagination={false}
            dataSource={usage.by_subject ?? []}
            columns={usageBySubjectColumns}
            locale={{ emptyText: 'No spend in this window' }}
          />
          {usageDays > 1 && (usage.by_day ?? []).length > 0 && (
            <Table
              size='small'
              rowKey='prog__day'
              pagination={false}
              dataSource={usage.by_day ?? []}
              columns={usageByDayColumns}
            />
          )}
        </div>
      </FormSection>

      <JsonFieldsModal
        open={paramsRow !== null}
        title={`Options · ${paramsRow?.internal_agent_task_models__task_key ?? ''}`}
        description={
          paramsRow
            ? `Passed to ${paramsRow.prog__model_name || paramsRow.internal_agent_task_models__llm_model_key} on every call for this task. Only the options this provider reads are shown.`
            : undefined
        }
        fields={paramSpecs}
        value={(paramsRow?.internal_agent_task_models__params as Record<string, unknown>) || {}}
        saving={saving}
        onCancel={() => setParamsRow(null)}
        onSave={saveParams}
      />

      <Modal
        title='Add task routing'
        open={newRouting !== null}
        onCancel={() => setNewRouting(null)}
        onOk={handleAddRouting}
        okText='Add'
        confirmLoading={saving}
        okButtonProps={{
          disabled: !newRouting?.agent_type_id || !newRouting?.task_key || !newRouting?.llm_model_key
        }}
        width={520}
        destroyOnClose
      >
        <div className='grid grid-cols-1 gap-3'>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>Agent type</label>
            <Select
              className='w-full'
              placeholder='Select an agent type'
              value={newRouting?.agent_type_id}
              options={data.agentTypes.map((t) => ({
                value: t.internal_agent_types__id,
                label: t.internal_agent_types__name || t.internal_agent_types__agent_type_key
              }))}
              onChange={(v) => setNewRouting((prev) => ({ ...(prev || {}), agent_type_id: v }))}
            />
          </div>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>Task</label>
            <Select
              className='w-full'
              placeholder='Select a task'
              value={newRouting?.task_key}
              options={data.taskKeys.map((k) => ({ value: k, label: k }))}
              onChange={(v) => setNewRouting((prev) => ({ ...(prev || {}), task_key: v }))}
            />
            <Text type='secondary' style={{ fontSize: 12 }}>
              Only tasks the runner actually dispatches are listed.
            </Text>
          </div>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>Model</label>
            <Select
              className='w-full'
              placeholder='Select a model'
              value={newRouting?.llm_model_key}
              options={modelOptions}
              onChange={(v) => setNewRouting((prev) => ({ ...(prev || {}), llm_model_key: v }))}
            />
          </div>
        </div>
      </Modal>

      <Modal
        title={modelDraftId ? 'Edit model' : 'Add model'}
        open={modelDraft !== null}
        onCancel={() => {
          setModelDraft(null)
          setModelDraftId(null)
        }}
        onOk={handleSaveModel}
        okText='Save'
        confirmLoading={saving}
        width={640}
        destroyOnClose
      >
        <div className='grid grid-cols-2 gap-3'>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>Model key</label>
            <Input
              value={modelDraft?.model_key ?? ''}
              placeholder='anthropic-opus'
              onChange={(e) =>
                setModelDraft((prev) => (prev ? { ...prev, model_key: e.target.value } : prev))
              }
            />
            <Text type='secondary' style={{ fontSize: 12 }}>
              The name task routing refers to.
            </Text>
          </div>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>Provider</label>
            <Select
              className='w-full'
              value={modelDraft?.provider}
              options={(data.providers.length ? data.providers : ['anthropic', 'openai']).map((p) => ({
                value: p,
                label: p
              }))}
              onChange={(v) => setModelDraft((prev) => (prev ? { ...prev, provider: v } : prev))}
            />
          </div>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>API model id</label>
            <Input
              value={modelDraft?.model_name ?? ''}
              placeholder='claude-opus-5'
              onChange={(e) =>
                setModelDraft((prev) => (prev ? { ...prev, model_name: e.target.value } : prev))
              }
            />
            <Text type='secondary' style={{ fontSize: 12 }}>
              Exactly as the provider names it. A wrong id fails every call for this model.
            </Text>
          </div>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>API key variable</label>
            <Input
              value={modelDraft?.api_key_secret_name ?? ''}
              placeholder='ANTHROPIC_API_KEY'
              onChange={(e) =>
                setModelDraft((prev) =>
                  prev ? { ...prev, api_key_secret_name: e.target.value } : prev
                )
              }
            />
            <Text type='secondary' style={{ fontSize: 12 }}>
              Names a backend environment variable. The key itself never enters the database.
            </Text>
          </div>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>Input cost per Mtok ($)</label>
            <InputNumber
              min={0}
              style={{ width: 160 }}
              value={modelDraft?.input_cost_per_mtok ?? undefined}
              onChange={(v) =>
                setModelDraft((prev) =>
                  prev ? { ...prev, input_cost_per_mtok: typeof v === 'number' ? v : null } : prev
                )
              }
            />
          </div>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>Output cost per Mtok ($)</label>
            <InputNumber
              min={0}
              style={{ width: 160 }}
              value={modelDraft?.output_cost_per_mtok ?? undefined}
              onChange={(v) =>
                setModelDraft((prev) =>
                  prev ? { ...prev, output_cost_per_mtok: typeof v === 'number' ? v : null } : prev
                )
              }
            />
          </div>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>Default max output tokens</label>
            <InputNumber
              min={256}
              max={200000}
              style={{ width: 160 }}
              value={modelDraft?.max_output_tokens_default ?? undefined}
              onChange={(v) =>
                setModelDraft((prev) =>
                  prev
                    ? { ...prev, max_output_tokens_default: typeof v === 'number' ? v : null }
                    : prev
                )
              }
            />
          </div>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>Description</label>
            <Input
              value={modelDraft?.description ?? ''}
              onChange={(e) =>
                setModelDraft((prev) => (prev ? { ...prev, description: e.target.value } : prev))
              }
            />
          </div>
        </div>
      </Modal>
    </div>
  )
}
